import { FC, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Card,
  CardMedia,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { X as XIcon } from 'react-feather';

interface DownloadAppModalProps {
  open: boolean;
  onClose: () => void;
}

const DownloadAppModal: FC<DownloadAppModalProps> = ({ open, onClose }) => {
  const qrCodeUrl = '/static/qr-code-app-download.png'; // Will store the QR code image
  const videoUrl = 'https://www.youtube.com/embed/8-3Xyz7Gs-w?hd=1';
  const [platform, setPlatform] = useState<'ios' | 'android'>('ios');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, color: 'text.primary' }}
        >
          Download the ReInvestWealth Mobile App
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'action.hover',
              color: 'error.main',
            },
          }}
        >
          <XIcon size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 3 }}>
        {/* QR Code and Instructions Section */}
        <Box
          sx={{
            backgroundColor: 'background.default',
            borderRadius: 2,
            p: 3,
            mb: 3,
          }}
        >
          <Grid container spacing={3} alignItems="center">
            {/* QR Code */}
            <Grid item xs={12} sm={5} sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  backgroundColor: 'background.paper',
                  borderRadius: 2,
                  p: 2,
                  display: 'inline-block',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
              >
                <Box
                  component="img"
                  src={qrCodeUrl}
                  alt="QR Code to Download App"
                  sx={{
                    width: { xs: 180, sm: 200 },
                    height: { xs: 180, sm: 200 },
                    display: 'block',
                  }}
                  onError={(e) => {
                    // Fallback if image doesn't exist - create QR code inline
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      parent.innerHTML = `
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" width="200" height="200">
                          <rect width="200" height="200" fill="white"/>
                          <path d="M20,20 h40 v40 h-40 z M70,20 h10 v10 h-10 z M90,20 h10 v10 h-10 z M110,20 h10 v10 h-10 z M140,20 h40 v40 h-40 z M30,30 h20 v20 h-20 z M150,30 h20 v20 h-20 z M20,70 h10 v10 h-10 z M40,70 h10 v10 h-10 z M60,70 h10 v10 h-10 z M80,70 h10 v10 h-10 z M100,70 h10 v10 h-10 z M120,70 h10 v10 h-10 z M170,70 h10 v10 h-10 z M20,90 h10 v10 h-10 z M40,90 h30 v10 h-30 z M90,90 h10 v10 h-10 z M110,90 h20 v10 h-20 z M150,90 h10 v10 h-10 z M170,90 h10 v10 h-10 z M20,110 h10 v10 h-10 z M50,110 h10 v10 h-10 z M70,110 h30 v10 h-30 z M120,110 h10 v10 h-10 z M150,110 h10 v10 h-10 z M170,110 h10 v10 h-10 z M20,140 h40 v40 h-40 z M70,140 h10 v10 h-10 z M90,140 h20 v10 h-20 z M120,140 h10 v10 h-10 z M140,140 h40 v40 h-40 z M30,150 h20 v20 h-20 z M80,150 h10 v10 h-10 z M150,150 h20 v20 h-20 z M70,170 h30 v10 h-30 z M110,170 h20 v10 h-20 z" fill="black"/>
                        </svg>
                      `;
                    }
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  mt: 1,
                  display: 'block',
                }}
              >
                Scan with your phone camera
              </Typography>
            </Grid>

            {/* Instructions */}
            <Grid item xs={12} sm={7}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 2,
                }}
              >
                Get Started in 3 Easy Steps:
              </Typography>
              <ToggleButtonGroup
                color="secondary"
                exclusive
                value={platform}
                onChange={(_event, value) => {
                  if (value) {
                    setPlatform(value);
                  }
                }}
                size="small"
                sx={{ mb: 2 }}
                aria-label="Select platform"
              >
                <ToggleButton value="ios" aria-label="iOS">
                  iPhone (iOS)
                </ToggleButton>
                <ToggleButton value="android" aria-label="Android">
                  Android
                </ToggleButton>
              </ToggleButtonGroup>
              <Box component="ol" sx={{ pl: 2.5, m: 0 }}>
                <Typography
                  component="li"
                  variant="body2"
                  sx={{ color: 'text.primary', mb: 1.5, lineHeight: 1.6 }}
                >
                  <strong>Scan the QR code with your phone's camera</strong> and
                  open the link
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  sx={{ color: 'text.primary', mb: 1.5, lineHeight: 1.6 }}
                >
                  <strong>Install the app</strong>
                  {platform === 'ios'
                    ? ' by tapping the "Share" icon from the Safari browser menu and choosing Add to Home Screen'
                    : ' by tapping Install or Add to Home screen from the prompt or browser menu'}
                  .
                </Typography>
                <Typography
                  component="li"
                  variant="body2"
                  sx={{ color: 'text.primary', mb: 1.5, lineHeight: 1.6 }}
                >
                  <strong>Optional:</strong> watch the video below for a quick
                  walkthrough.
                </Typography>
              </Box>
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(88, 80, 236, 0.08)'
                      : 'secondary.light',
                  borderRadius: 1,
                  borderLeft: '4px solid',
                  borderColor: 'secondary.main',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'text.primary'
                        : 'text.primary',
                    fontWeight: 500,
                  }}
                >
                  💡 Note: For iOS users, make sure to open the link in Safari.
                </Typography>
              </Box>
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(88, 80, 236, 0.08)'
                      : 'secondary.light',
                  borderRadius: 1,
                  borderLeft: '4px solid',
                  borderColor: 'secondary.main',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'text.primary'
                        : 'text.primary',
                    fontWeight: 500,
                  }}
                >
                  💡 Tip: Snap receipts instantly on-the-go to never miss a tax
                  deduction!
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Video Section */}
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: 1,
            }}
          >
            How to Use the Mobile App
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mb: 2,
            }}
          >
            Watch this quick tutorial to learn how to maximize your mobile
            experience and capture expenses effortlessly.
          </Typography>
          <Card
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          >
            <CardMedia
              component="iframe"
              sx={{
                minHeight: { xs: '250px', sm: '400px' },
                width: '100%',
                height: 'auto',
                border: 'none',
              }}
              src={videoUrl}
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </Card>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DownloadAppModal;
